import { useSidebarState } from '@src/hooks/useSidebarState';
import HomeIcon from '@src/svgs/HomeIcon';
import SpeakersIcon from '@src/svgs/SpeakersIcon';
import StoryManagerIcon from '@src/svgs/StoryManagerIcon';
import { AnimatePresence, motion } from 'framer-motion';
import React from 'react';
import { NavLink } from 'react-router-dom';
import styled, { css } from 'styled-components';

import { Studio } from '../types.ts/general';
import {
  LABEL_VARIANTS,
  SECTION_LABEL_VARIANTS,
  SIDEBAR_SPRING,
} from './sidebar/sidebarMotion';
import { SidebarTooltip } from './SidebarTooltip';

interface NavItemConfig {
  studio: Studio;
  label: string;
  icon: React.ReactNode;
}

const PRIMARY_NAV_ITEMS: NavItemConfig[] = [
  { studio: Studio.home, label: 'Home', icon: <HomeIcon /> },
  {
    studio: Studio.dashboard,
    label: 'Story Manager',
    icon: <StoryManagerIcon />,
  },
  {
    studio: Studio.speakers,
    label: 'Speakers',
    icon: <SpeakersIcon />,
  },
];

interface MainMenuNavListProps {
  currentStudio: Studio;
  collapsed: boolean;
  allowHome: boolean;
  allowStoryManager: boolean;
  allowSpeakerManager: boolean;
  search: string;
}

const MainMenuNavList: React.FC<MainMenuNavListProps> = ({
  currentStudio,
  collapsed,
  allowHome,
  allowStoryManager,
  allowSpeakerManager,
  search,
}) => {
  const shouldShowNavItem = (item: NavItemConfig) => {
    if (!allowHome && item.studio === Studio.home) return false;
    if (!allowStoryManager && item.studio === Studio.dashboard) return false;
    if (!allowSpeakerManager && item.studio === Studio.speakers) return false;
    return true;
  };

  const { showLabels } = useSidebarState();

  return (
    <NavList role="navigation" aria-label="Main navigation">
      <SectionLabelSlot>
        <AnimatePresence initial={false}>
          {showLabels && (
            <motion.div
              key="discover"
              variants={SECTION_LABEL_VARIANTS}
              initial="hidden"
              animate="shown"
              exit="hidden"
              transition={SIDEBAR_SPRING}
            >
              <SectionLabel>Discover</SectionLabel>
            </motion.div>
          )}
        </AnimatePresence>
      </SectionLabelSlot>
      {PRIMARY_NAV_ITEMS.filter(shouldShowNavItem).map(item => (
        <SidebarNavItem
          key={item.studio}
          to={`/${item.studio}${search}`}
          $isSelected={currentStudio === item.studio}
          $collapsed={collapsed}
          data-testid={`nav-item-${item.studio}`}
        >
          <NavIcon>{item.icon}</NavIcon>
          <AnimatePresence initial={false}>
            {showLabels && (
              <motion.span
                key="label"
                variants={LABEL_VARIANTS}
                initial="hidden"
                animate="shown"
                exit="hidden"
                transition={SIDEBAR_SPRING}
                style={{ overflow: 'hidden', whiteSpace: 'nowrap' }}
              >
                {item.label}
              </motion.span>
            )}
          </AnimatePresence>
          {!showLabels && <Tooltip>{item.label}</Tooltip>}
        </SidebarNavItem>
      ))}
    </NavList>
  );
};

export default MainMenuNavList;

const NavList = styled.div`
  display: flex;
  flex-direction: column;
  padding: 8px 10px 4px;
  gap: 4px;
`;

const SectionLabelSlot = styled.div`
  /* Reserve a fixed band equal to SectionLabel height so items below stay
   * at the same Y whether the label is shown or hidden. */
  height: 28px;
`;

const SectionLabel = styled.div`
  display: flex;
  align-items: flex-end;
  height: 28px;
  box-sizing: border-box;
  color: var(--text-muted);
  font-size: 11px;
  line-height: 14px;
  font-weight: var(--font-weight-medium);
  letter-spacing: 0.2px;
  padding: 0 8px 6px 12px;
  white-space: nowrap;
  overflow: hidden;
`;

const Tooltip = styled(SidebarTooltip)``;

const SidebarNavItem = styled(NavLink)<{
  $isSelected: boolean;
  $collapsed: boolean;
}>`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  height: 30px;
  box-sizing: border-box;
  gap: 10px;
  /* Constant padding so the icon's X stays put when sidebar width animates.
   * Toggling justify-content/padding on $collapsed causes a horizontal
   * jump during the width transition. */
  padding: 0 10px;
  border-radius: 6px;
  text-decoration: none;
  font-size: 13px;
  font-weight: var(--font-weight-medium);
  color: var(--text-secondary);
  transition:
    background var(--transition-fast) ease,
    color var(--transition-fast) ease;
  cursor: pointer;
  border: none !important;

  &:hover {
    background: var(--sidebar-hover);
    color: var(--text-secondary);
  }

  &:hover ${Tooltip} {
    opacity: 1;
  }

  ${p =>
    p.$isSelected &&
    css`
      background: var(--sidebar-active);
      color: var(--foreground);

      & > span:first-child {
        color: var(--accent);
      }
    `}
`;

const NavIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  color: inherit;

  svg {
    width: 100%;
    height: 100%;
  }
`;
